<template>
  <div class="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
    <button type="button" @click="expanded = !expanded"
      class="w-full flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
      <div class="flex items-center gap-3 min-w-[180px]">
        <span class="w-3 h-3 rounded-full flex-shrink-0" :class="booking.color"></span>
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{{ booking.host_ui_booking_time }}</p>
          <p v-if="booking.is_rescheduled" class="text-xs italic text-gray-400">{{ I18N.application.bookings.upcoming.rescheduled }}</p>
        </div>
      </div>

      <div class="flex-1 min-w-[160px] text-sm">
        <p class="text-gray-900 dark:text-gray-200 truncate">{{ booking.attendee_name }} <span class="text-gray-400">({{ booking.attendee_email }})</span></p>
        <p class="text-gray-400 truncate">{{ booking.event_name }}</p>
      </div>

      <span class="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap" :class="statusBadgeClass">
        {{ statusLabel }}
      </span>

      <div class="relative flex items-center gap-1">
        <button @click.stop="menuOpen = !menuOpen"
          class="p-1.5 rounded-lg transition-colors"
          :class="menuOpen ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/>
          </svg>
        </button>
        <BookingActionsMenu v-if="menuOpen" :booking="booking" :category="category" position="right"
          @edit-notes="openNotesEditor"
          @cancel="menuOpen = false; cancelModalOpen = true"
          @archive="archive"
          @unarchive="unarchive"
          @close="menuOpen = false" />

        <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': expanded }"
          fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
        </svg>
      </div>
    </button>

    <div v-if="expanded" class="px-4 sm:px-6 pb-6">
      <div class="md:flex md:divide-x md:divide-gray-100 md:dark:divide-gray-700">
        <div class="md:w-5/12 md:pr-6 space-y-3 text-sm">
          <p class="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
            </svg>
            {{ booking.host_formatted_target_date }}
          </p>
          <p class="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
            </svg>
            {{ booking.host_ui_booking_time }} ({{ booking.ui_duration }})
          </p>
          <p class="flex items-center gap-3 text-gray-500 dark:text-gray-400 break-all">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path :d="locationIconPath" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span v-html="booking.formatted_location_name"></span>
          </p>
          <p class="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"/>
            </svg>
            {{ booking.titleize_host_timezone }}
          </p>
          <p class="text-xs text-gray-400 italic">{{ I18N.application.bookings.upcoming.created_on }} {{ booking.host_formatted_created_date }}</p>

          <div v-if="booking.is_rescheduled && booking.host_former_booking_details?.formatted_timings" class="pt-3 border-t border-gray-100 dark:border-gray-700">
            <p class="font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{{ I18N.application.bookings.upcoming.former_time }}</p>
            <p class="text-gray-500 dark:text-gray-400">{{ booking.host_former_booking_details.formatted_timings }}</p>
            <p class="text-gray-500 dark:text-gray-400">{{ booking.host_former_booking_details.formatted_timezone }}</p>
          </div>

          <div v-if="booking.canceled_reason" class="pt-3 border-t border-gray-100 dark:border-gray-700">
            <p class="font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{{ I18N.application.bookings.upcoming.reason }}</p>
            <p class="text-gray-500 dark:text-gray-400">{{ booking.canceled_reason }}</p>
          </div>
        </div>

        <div class="md:w-7/12 md:pl-6 mt-5 md:mt-0 text-sm space-y-2">
          <h5 class="font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ I18N.application.bookings.attendee_details }}</h5>
          <p class="text-gray-600 dark:text-gray-400">
            <span class="font-semibold text-gray-700 dark:text-gray-300">{{ I18N.application.bookings.attendee }}:</span>
            <a :href="`/attendees?attendee=${booking.attendee_email}`" data-turbo="false" class="text-blue-600 hover:text-blue-700 ml-1">{{ booking.attendee_name }} ({{ booking.attendee_email }})</a>
          </p>
          <p class="text-gray-600 dark:text-gray-400"><span class="font-semibold text-gray-700 dark:text-gray-300">{{ I18N.application.bookings.time }}:</span> {{ booking.attendee_ui_booking_range_with_date }}</p>
          <p class="text-gray-600 dark:text-gray-400"><span class="font-semibold text-gray-700 dark:text-gray-300">{{ I18N.application.bookings.timezone }}:</span> {{ booking.attendee_timezone_titleize }}</p>
          <p class="text-gray-600 dark:text-gray-400"><span class="font-semibold text-gray-700 dark:text-gray-300">{{ I18N.application.bookings.location }}:</span> {{ booking.attendee_location }}</p>

          <div v-if="booking.formatted_description" class="pt-2">
            <p class="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-1">{{ I18N.application.bookings.upcoming.question_text }}</p>
            <p class="text-gray-600 dark:text-gray-400 break-words" v-html="booking.formatted_description"></p>
          </div>

          <BookingInternalNotesEditor v-if="editingNotes"
            :event-id="eventId" :booking-id="booking.id" v-model="booking.internal_notes" :flash="flash"
            @close="editingNotes = false" />

          <div v-else class="pt-2">
            <button v-if="!booking.internal_notes" @click="editingNotes = true"
              class="flex items-center gap-1.5 text-blue-600 text-sm hover:text-blue-700">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              {{ I18N.application.bookings.add_notes }}
              <span class="text-gray-400 font-normal">({{ I18N.application.bookings.only_host_see }})</span>
            </button>
            <div v-else>
              <div class="flex items-center gap-2 mb-1">
                <p class="font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ I18N.application.bookings.meeting_notes }}</p>
                <button @click="editingNotes = true" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                  </svg>
                </button>
              </div>
              <div class="text-gray-600 dark:text-gray-400" v-html="booking.internal_notes"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BookingCancelModal v-if="cancelModalOpen" :event-id="eventId" :booking="booking" :flash="flash"
      @close="cancelModalOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useConstants } from 'composables/useConstants.js'
import { useEventBookingsStore } from 'stores/eventBookings.js'
import BookingActionsMenu from 'controllers/vue_components/events/bookings/BookingActionsMenu'
import BookingInternalNotesEditor from 'controllers/vue_components/events/bookings/BookingInternalNotesEditor'
import BookingCancelModal from 'controllers/vue_components/events/bookings/BookingCancelModal'

const props = defineProps({
  booking: { type: Object, required: true },
  category: { type: String, required: true },
  eventId: { type: [String, Number], required: true },
  flash: { type: Object, default: null },
})

const { I18N } = useConstants()
const store = useEventBookingsStore()

const expanded = ref(false)
const menuOpen = ref(false)
const editingNotes = ref(false)
const cancelModalOpen = ref(false)

const STATUS_BADGES = {
  confirmed: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  canceled: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  rescheduled: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  external_payment_in_pending: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  payment_rejected: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
}

const statusBadgeClass = computed(() => STATUS_BADGES[props.booking.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300')
const statusLabel = computed(() => (props.booking.status || '').replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()))

const locationIconPath = computed(() =>
  props.booking.location_details?.name === 'phone_call'
    ? 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z'
    : 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z'
)

function closeMenu() {
  if (menuOpen.value) menuOpen.value = false
}

onMounted(() => document.addEventListener('click', closeMenu))
onUnmounted(() => document.removeEventListener('click', closeMenu))

function openNotesEditor() {
  menuOpen.value = false
  expanded.value = true
  editingNotes.value = true
}

async function archive() {
  menuOpen.value = false
  const result = await store.archiveBooking(props.eventId, props.booking.id)
  flashResult(result, I18N.notifications.bookings.booking_archived)
}

async function unarchive() {
  menuOpen.value = false
  const result = await store.unarchiveBooking(props.eventId, props.booking.id)
  flashResult(result, I18N.notifications.bookings.booking_back)
}

function flashResult(result, fallbackMessage) {
  if (!props.flash) return
  if (result.success) {
    props.flash.value?.show(result.message || fallbackMessage, 'notice')
  } else {
    props.flash.value?.show(result.message, 'alert')
  }
}
</script>
